// Shared EPD validity helpers.
//
// An EPD is considered "expired" when its validity-end date (`validTo`) is in the past.
// Expired EPDs should not be relied on for compliant LCA calculations, so this is used
// both to flag individual library cards and to surface expired mappings at the project level.

// An EPD counts as "expiring soon" when its validity ends within this many days from now.
// ~6 months: long enough to plan a swap before the EPD actually lapses.
export const EPD_EXPIRING_SOON_DAYS = 180;

export type EpdValidityStatus = "expired" | "expiringSoon" | "valid";

/**
 * Returns true when the given EPD validity-end date is in the past.
 * Safe for missing/empty/invalid dates — those return false (treated as "not expired").
 */
export function isEpdExpired(validTo?: string | null): boolean {
  if (!validTo) return false;
  const validToDate = new Date(validTo);
  if (isNaN(validToDate.getTime())) return false;
  return validToDate.getTime() < Date.now();
}

/**
 * Returns true when the EPD is not yet expired but its validity ends within
 * EPD_EXPIRING_SOON_DAYS. Safe for missing/empty/invalid dates — those return false.
 */
export function isEpdExpiringSoon(validTo?: string | null): boolean {
  if (!validTo) return false;
  const validToDate = new Date(validTo);
  if (isNaN(validToDate.getTime())) return false;
  const msUntilExpiry = validToDate.getTime() - Date.now();
  if (msUntilExpiry < 0) return false; // already expired
  return msUntilExpiry <= EPD_EXPIRING_SOON_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Classifies an EPD by its validity-end date: "expired" (past), "expiringSoon"
 * (within EPD_EXPIRING_SOON_DAYS), or "valid" (everything else, incl. missing dates).
 */
export function getEpdValidityStatus(validTo?: string | null): EpdValidityStatus {
  if (isEpdExpired(validTo)) return "expired";
  if (isEpdExpiringSoon(validTo)) return "expiringSoon";
  return "valid";
}
